import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Quote, Play, Instagram, X, GraduationCap } from 'lucide-react';
import { mezunlar, biyolojiGaleri } from '../../data/mezunYorumlari';
import { useTranslation } from 'react-i18next';

const DEPT_CONFIG = {
  'Kimya':              { accent: '#3b82f6', badge: 'bg-blue-50 text-blue-700 border-blue-100' },
  'Biyoloji':           { accent: '#22c55e', badge: 'bg-green-50 text-green-700 border-green-100' },
  'Matematik':          { accent: '#a855f7', badge: 'bg-purple-50 text-purple-700 border-purple-100' },
  'İstatistik':         { accent: '#f59e0b', badge: 'bg-amber-50 text-amber-700 border-amber-100' },
  'Aktüerya Bilimleri': { accent: '#f43f5e', badge: 'bg-rose-50 text-rose-700 border-rose-100' },
};

const DEPTS_TR = ['Tümü', 'Kimya', 'Matematik', 'Aktüerya Bilimleri', 'İstatistik', 'Biyoloji'];
const DEPTS_EN = ['All', 'Chemistry', 'Mathematics', 'Actuarial Sciences', 'Statistics', 'Biology'];
const DEPT_MAP_EN_TO_TR = {
  'All': 'Tümü', 'Chemistry': 'Kimya', 'Mathematics': 'Matematik',
  'Actuarial Sciences': 'Aktüerya Bilimleri', 'Statistics': 'İstatistik', 'Biology': 'Biyoloji',
};

const isRealPhoto = url =>
  url && (url.startsWith('http') || url.startsWith('https')) && !url.includes('unsplash');

// ── Modal ─────────────────────────────────────────────────────────────────────
const Modal = ({ mezun, onClose }) => {
  const cfg = DEPT_CONFIG[mezun.bolum] || { accent: '#7B0D1E', badge: 'bg-gray-100 text-gray-700 border-gray-200' };
  const deptLabel = mezun.bolum === 'Aktüerya Bilimleri' ? 'Aktüerya' : mezun.bolum;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="h-1" style={{ background: cfg.accent }} />
        <div className="p-6 md:p-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X size={15} className="text-gray-600" />
          </button>

          {/* Kişi bilgisi */}
          <div className="flex items-center gap-4 mb-6">
            <img
              src={mezun.foto} alt={mezun.isim}
              className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-gray-100"
              onError={e => { e.target.src = '/mezuniyet.png'; }}
            />
            <div>
              <p className="font-bold text-gray-900 text-base leading-tight">{mezun.isim}</p>
              {mezun.pozisyon && <p className="text-gray-500 text-sm mt-0.5">{mezun.pozisyon}</p>}
              {mezun.kurum && <p className="text-gray-400 text-xs mt-0.5">{mezun.kurum}</p>}
              <span className={`inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.badge}`}>
                {deptLabel}
              </span>
            </div>
          </div>

          {/* Tam yorum */}
          <div className="relative">
            <Quote size={28} className="absolute -top-1 -left-1 opacity-10" style={{ color: cfg.accent }} />
            <p className="text-gray-700 leading-relaxed italic pl-4 text-sm">
              "{mezun.yorum}"
            </p>
          </div>

          {mezun.mezuniyetYili && (
            <div className="flex items-center gap-1.5 mt-5 text-xs text-gray-400">
              <GraduationCap size={13} />
              <span>Mezuniyet: {mezun.mezuniyetYili}</span>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Alumni Quote Card ─────────────────────────────────────────────────────────
const AlumniCard = ({ mezun, index, onClick }) => {
  const cfg = DEPT_CONFIG[mezun.bolum] || { accent: '#7B0D1E', badge: 'bg-gray-100 text-gray-700 border-gray-200' };
  const excerpt = mezun.yorum.length > 160 ? mezun.yorum.slice(0, 157) + '…' : mezun.yorum;
  const deptLabel = mezun.bolum === 'Aktüerya Bilimleri' ? 'Aktüerya' : mezun.bolum;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      className="relative flex flex-col bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group cursor-pointer"
    >
      <div className="h-[3px]" style={{ background: cfg.accent }} />
      <div className="flex flex-col flex-1 p-5 md:p-6">
        <Quote size={22} className="mb-3 opacity-15" style={{ color: cfg.accent }} />
        <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-5 italic">
          "{excerpt}"
        </p>
        <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
          <img
            src={mezun.foto} alt={mezun.isim}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-gray-100 border border-gray-100"
            onError={e => { e.target.src = '/mezuniyet.png'; }}
          />
          <div className="min-w-0 flex-1">
            <p className="text-gray-900 font-semibold text-sm leading-tight truncate">{mezun.isim}</p>
            <p className="text-gray-400 text-xs mt-0.5 leading-tight truncate">
              {mezun.pozisyon}{mezun.kurum ? ` · ${mezun.kurum}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.badge}`}>
              {deptLabel}
            </span>
            <span className="text-[10px] text-gray-400 group-hover:text-[#7B0D1E] transition-colors font-medium">
              Tümü →
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Biyoloji Video Card ───────────────────────────────────────────────────────
const BiyolojiVideoCard = ({ item, index }) => (
  <motion.a
    href={item.link}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
    className="flex flex-col bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
  >
    <div className="relative overflow-hidden aspect-video">
      <img
        src={item.foto} alt={item.baslik}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
        <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <Play size={18} className="text-white fill-white ml-0.5" />
        </div>
      </div>
      <div className="absolute top-2.5 left-2.5 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
        <Instagram size={9} /> Reel
      </div>
    </div>
    <div className="p-3.5">
      <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{item.baslik}</p>
    </div>
  </motion.a>
);

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
const MezunYorumlari = () => {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === 'en';
  const depts = isEN ? DEPTS_EN : DEPTS_TR;
  const [activeLabel, setActiveLabel] = useState(depts[0]);
  const [openMezun, setOpenMezun] = useState(null);

  const activeTR = isEN ? (DEPT_MAP_EN_TO_TR[activeLabel] || 'Tümü') : activeLabel;
  const isBiyoloji = activeTR === 'Biyoloji';
  const isTumu    = activeTR === 'Tümü';
  const showVideos = isTumu || isBiyoloji;

  const alumni = useMemo(() => {
    if (showVideos) return [];
    const pool = mezunlar.filter(m => m.yorum && m.yorum.trim().length > 20);
    return pool.filter(m => m.bolum === activeTR && isRealPhoto(m.foto)).slice(0, 9);
  }, [activeTR, showVideos]);

  const totalCount = mezunlar.filter(m => m.yorum?.trim().length > 20).length;

  return (
    <section className="py-20 lg:py-24 bg-[#faf7f2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Başlık */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#7B0D1E] mb-2">
              {t('home.alumni.title')}
            </p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
              {isEN ? (
                <>Hear It From <span className="text-[#7B0D1E]">Our Alumni</span></>
              ) : (
                <>Başarıyı Onlardan <span className="text-[#7B0D1E]">Dinleyin</span></>
              )}
            </h2>
            <p className="text-gray-400 text-sm mt-2 max-w-md leading-relaxed">
              {t('home.alumni.subtitle')}
            </p>
          </div>
          <Link
            to="/mezunlar"
            className="flex-shrink-0 flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-[#7B0D1E] transition-colors duration-200 pb-1"
          >
            {isEN ? `View All ${totalCount}+` : `${totalCount}+ Mezunumuzu Gör`}
            <ChevronRight size={15} />
          </Link>
        </div>

        {/* Filtre Pilleri */}
        <div className="flex flex-wrap gap-2 mb-8">
          {depts.map(dept => (
            <button
              key={dept}
              onClick={() => setActiveLabel(dept)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
                activeLabel === dept
                  ? 'bg-[#7B0D1E] text-white border-[#7B0D1E] shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#7B0D1E]/30 hover:text-[#7B0D1E]'
              }`}
            >
              {dept}
            </button>
          ))}
        </div>

        {/* Kartlar */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeLabel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {showVideos
              ? biyolojiGaleri.slice(0, isTumu ? 6 : 9).map((item, i) => (
                  <BiyolojiVideoCard key={item.id} item={item} index={i} />
                ))
              : alumni.map((m, i) => (
                  <AlumniCard key={m.id} mezun={m} index={i} onClick={() => setOpenMezun(m)} />
                ))
            }
          </motion.div>
        </AnimatePresence>

      </div>

      {/* Modal */}
      <AnimatePresence>
        {openMezun && (
          <Modal mezun={openMezun} onClose={() => setOpenMezun(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default MezunYorumlari;
